// Equine product catalog used by the Build An Order screen.
//
// W36 (2026-10-20): this file carries NO prices. It is the display list
// (name, blurb, size, tile colours) for the six products the pricing
// engine knows (services/api-server/pricing/sku-variant-lookup.mjs
// PRODUCT_SKU_FAMILIES, keyed by `handle` below). Every number shown on
// the screen is the engine's output (feedback_pricing_engine_authority):
//   - real sessions read unit prices from POST /v1/pricing/calculate
//     (data/livePricing.ts) and never from the design fixture;
//   - the design preview (?preview=sarah, dev mock) merges the example
//     prices from data/mockPricing.ts at the screen boundary only.
// The MOCK_PRICING import that used to live here is gone so a real
// session cannot reach a fixture price by accident.

export interface CatalogProduct {
  /** Pricing-engine product key (canonical handle). */
  handle: string;
  /** Display name; also the quantities key the screen uses. */
  name: string;
  detail: string;
  size: string;
  tint: string;
  color: string;
  square: string;
}

export interface CatalogEntry extends CatalogProduct {
  initialQty: number;
}

// Order mirrors the approved HTML display order. initialQty is the
// design-preview starting basket (Sarah's example); real sessions start
// every product at 0 (W32).
export const CATALOG_PRODUCTS: CatalogEntry[] = [
  { handle: 'gut-x', name: 'Gut X', detail: 'Digestive support', size: '1 gallon', tint: '#fff0cf', color: '#a87922', square: 'GX', initialQty: 2 },
  { handle: 'osteo-max', name: 'Osteo-MAX', detail: 'Bone support', size: '1 gallon', tint: '#e8f2df', color: '#628252', square: 'OM', initialQty: 1 },
  { handle: 'joint-flex-plus', name: 'Joint Flex Plus', detail: 'Joint support', size: '1 gallon', tint: '#e4edff', color: '#547fb1', square: 'JF', initialQty: 0 },
  { handle: 'amino-max', name: 'Amino-MAX', detail: 'Amino acid support', size: '1 gallon', tint: '#f0e5f8', color: '#916da7', square: 'AM', initialQty: 0 },
  { handle: 'hydra-max', name: 'Hydra-MAX', detail: 'Hydration support', size: '1 gallon', tint: '#dbf4f6', color: '#328a94', square: 'HM', initialQty: 0 },
  { handle: 'max-calm', name: 'Max Calm', detail: 'Calming support', size: '1 gallon', tint: '#fae8dd', color: '#b77d50', square: 'MC', initialQty: 0 },
];

/** Kept for existing importers; same list as CATALOG_PRODUCTS. */
export const EQUINE_CATALOG: CatalogEntry[] = CATALOG_PRODUCTS;

export function catalogByHandle(handle: string): CatalogEntry | undefined {
  return CATALOG_PRODUCTS.find((p) => p.handle === handle);
}

export type CategoryKey = 'Equine' | 'Human' | 'Canine' | 'Equipment';

// SVG tile for product art. Each product gets a tinted square with its
// three-letter mark; the Drive-hosted jug images (per
// reference_100x_equine_product_jugs) ship once CDN URLs land.
export function productTile(entry: CatalogProduct): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
      <rect width="56" height="56" rx="9" fill="${entry.tint}"/>
      <text x="28" y="33" text-anchor="middle" font-family="Workspace Sans, Inter, Arial" font-size="12" font-weight="700" fill="${entry.color}">${entry.square}</text>
    </svg>`;
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg.trim());
}
